import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, mediaSrc } from "../api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/Logo";
import { PageHeader, ProgressBar, StatCard } from "../components/ui";
import { AvatarUpload } from "../components/Uploads";
import { BookOpen, Zap, Flag, Award, Trophy, Save, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function Profile() {
  const { refresh } = useAuth();
  const [p, setP] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => { setP(r.data.profile); setForm(r.data.profile); }).catch(() => {});
    api.get("/analytics/me").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  if (!p) return <Spinner />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const saveAvatar = async (url) => {
    try {
      await api.patch("/profile", { avatar_url: url });
      setP({ ...p, avatar_url: url });
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/profile", {
        full_name: form.full_name, phone: form.phone, organization: form.organization,
        designation: form.designation, education: form.education, bio: form.bio, interests: form.interests,
      });
      toast.success("Profile updated");
      await refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="profile-page">
      <PageHeader overline="My account" title="Profile & stats" />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <div className="card p-6 text-center">
            <AvatarUpload value={p.avatar_url} name={p.full_name} onChange={saveAvatar} />
            <h2 className="font-heading text-xl font-bold mt-3">{p.full_name}</h2>
            <p className="text-slate-400 text-sm">{p.email}</p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="chip bg-[#F26522]/15 text-[#F26522] font-bold">Level {p.level}</span>
              <span className="chip bg-white/5 text-slate-300">{p.xp} XP</span>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-400 mb-1.5"><span>{p.xp_into_level}/{p.xp_for_next_level}</span><span>Lvl {p.level + 1}</span></div>
              <ProgressBar pct={p.progress_pct} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={BookOpen} label="Modules" value={p.stats.modules_completed} />
            <StatCard icon={Zap} label="Quizzes" value={p.stats.quizzes_passed} accent="#0ea5e9" />
            <StatCard icon={Flag} label="CTF" value={p.stats.ctf_solved} accent="#10B981" />
            <StatCard icon={Award} label="Certs" value={p.stats.certificates} accent="#F59E0B" />
          </div>

          {p.badges.length > 0 && (
            <div className="card p-6">
              <div className="overline mb-3 flex items-center gap-2"><Trophy size={14} /> Badges</div>
              <div className="flex flex-wrap gap-2">
                {p.badges.map((b) => (
                  <span key={b.badge_key} className="chip bg-purple-500/15 text-purple-300">
                    {b.badge_url && (
                      <img src={mediaSrc(b.badge_url)} alt="" className="h-16 w-16 rounded-full object-cover" />
                    )}
                    {b.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          {analytics?.xp_over_time?.length > 0 && (
            <div className="card p-6">
              <div className="overline mb-4 flex items-center gap-2"><TrendingUp size={14} /> XP over time</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={analytics.xp_over_time}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="day" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip contentStyle={{ background: "#121C30", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                  <Line type="monotone" dataKey="xp" stroke="#F26522" strokeWidth={2} dot={{ fill: "#F26522" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="card p-6">
            <div className="overline mb-4">Edit profile</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label">Full name</label><input className="input" value={form.full_name || ""} onChange={set("full_name")} data-testid="profile-name" /></div>
              <div><label className="label">Phone</label><input className="input" value={form.phone || ""} onChange={set("phone")} /></div>
              <div><label className="label">Organization</label><input className="input" value={form.organization || ""} onChange={set("organization")} /></div>
              <div><label className="label">Designation</label><input className="input" value={form.designation || ""} onChange={set("designation")} /></div>
              <div><label className="label">Education</label><input className="input" value={form.education || ""} onChange={set("education")} /></div>
              <div><label className="label">Interests</label><input className="input" value={form.interests || ""} onChange={set("interests")} placeholder="e.g. Web security, OSINT" /></div>
              <div className="sm:col-span-2"><label className="label">Bio</label><textarea className="input min-h-20" value={form.bio || ""} onChange={set("bio")} /></div>
            </div>
            <button className="btn-primary mt-4" onClick={save} disabled={busy} data-testid="profile-save"><Save size={16} /> Save changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}
